import { UploadOutlined } from '@ant-design/icons';
import { settingFieldType } from 'config/settingFieldType';
import { uploadFiles } from 'library/firebase/fb.utils';
import useManagePermittedRoles from 'library/hooks/useManagePermittedRoles';
import dayjs from 'dayjs';
import React from 'react';
import ReactJson from 'react-json-view';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker, Input, Segmented, Select, Switch, Tooltip, Upload } from 'zui/Antd';
import RenderCredential from '../RenderCredential';
import RenderMultiValue from '../RenderMultiValue';
import { theme } from 'antd';
import { innerRangeActions } from 'reduxStore/innerRange/slice';
import { controlActions } from 'reduxStore/control/slice';

const { TextArea } = Input;

const RenderFieldTypeSetting = ({
	setting,
	index,
	settings,
	setSettings,
	fileList,
	setFileList,
	handleRemoveUpload
}) => {
	const {
		token: { colorTextDescription }
	} = theme.useToken();
	const {
		remoteConfig: { entityInfo }
	} = useSelector((state) => state.FbRemoteConfig);
	const { orgID, personID } = useSelector((state) => state.Auth.selectedProfile);
	const dispatch = useDispatch();

	let value = [];

	const generateFilePath = async (file) => {
		const generatedPath =
			`${entityInfo['Settings'].entityDir}/` +
			orgID +
			personID +
			Date.now() +
			(file.type === 'image/png' ? '.png' : '.jpeg');
		return generatedPath;
	};

	const checkBeforeUpload = async (file) => {
		const filePath = await generateFilePath(file);
		await uploadFiles(filePath, file);

		const updatedSettings = [...settings];
		updatedSettings[index] = {
			...setting,
			value: filePath
		};
		setSettings(updatedSettings);
		setFileList([...fileList, file]);
	};

	const isPermittedEditJson = useManagePermittedRoles('editJsonSetting', {
		orgID: [orgID],
		siteID: [],
		floorID: []
	});

	const canEditSettings = useManagePermittedRoles('editSettings');

	const BUTTON_ACTIONS = {
		IR_SYNC: () => dispatch(innerRangeActions.triggerSync()),
		CONTROL_SYNC: () => dispatch(controlActions.triggerSync())
	};

	const handleButtonAction = (action) => {
		BUTTON_ACTIONS[action]?.();
	};

	const renderSettingByFieldType = (setting) => {
		switch (setting.fieldTypeID) {
			case settingFieldType.STRING:
				return (
					<Tooltip title={!canEditSettings ? "You don't have permission to edit settings" : ''}>
						<TextArea
							style={{ color: colorTextDescription }}
							placeholder={`Enter a ${setting.title}`}
							value={setting.value}
							disabled={!canEditSettings}
							onChange={(e) => {
								const newValue = e.target.value;
								const updatedSettings = [...settings];
								updatedSettings[index] = {
									...setting,
									value: newValue
								};
								setSettings(updatedSettings);
							}}
							className='fieldString'
							autoSize={{ minRows: 1, maxRows: 9 }}
						/>
					</Tooltip>
				);
			case settingFieldType.NUMBER:
				return (
					<Tooltip title={!canEditSettings ? "You don't have permission to edit settings" : ''}>
						<Input
							placeholder={`Enter a ${setting.title}`}
							value={setting.value}
							disabled={!canEditSettings}
							onChange={(e) => {
								const newValue = e.target.value;
								const updatedSettings = [...settings];
								updatedSettings[index] = {
									...setting,
									value: newValue
								};
								setSettings(updatedSettings);
							}}
							className='fieldString'
							autoSize
							type={setting.fieldTypeID === settingFieldType.STRING ? 'text' : 'number'}
						/>
					</Tooltip>
				);
			case settingFieldType.BOOLEAN:
				const boolOptions = setting.valueOptions ? JSON.parse(setting.valueOptions) : {};
				return (
					<Tooltip
						title={
							!canEditSettings ? "You don't have permission to edit settings" : boolOptions.tooltip || ''
						}
					>
						<div className='containerSwitch' style={boolOptions.style || {}}>
							<Switch
								checked={setting.value === 1}
								type='primary'
								disabled={!canEditSettings || boolOptions.disabled || false}
								onChange={(checked) => {
									if (boolOptions.disabled) return;
									const newValue = checked ? 1 : 0;
									const updatedSettings = [...settings];
									updatedSettings[index] = {
										...setting,
										value: newValue
									};
									setSettings(updatedSettings);
								}}
							/>
						</div>
					</Tooltip>
				);
			case settingFieldType.SINGLESELECT:
			case settingFieldType.MULTISELECT:
				if (setting.valueOptions.length > 4) {
					return (
						<Tooltip title={!canEditSettings ? "You don't have permission to edit settings" : ''}>
							<Select
								placeholder={`Select ${setting.title}`}
								options={setting.valueOptions.map((setting) => {
									return {
										label: setting,
										value: setting
									};
								})}
								mode='multiple'
								value={setting.value.length === 0 ? [] : setting.value}
								disabled={!canEditSettings}
								onChange={(selectedValues) => {
									const updatedSettings = [...settings];
									updatedSettings[index] = {
										...setting,
										value: selectedValues
									};
									setSettings(updatedSettings);
								}}
							/>
						</Tooltip>
					);
				} else {
					return (
						<Tooltip title={!canEditSettings ? "You don't have permission to edit settings" : ''}>
							<Segmented
								type='primary'
								options={setting.valueOptions}
								value={setting.value}
								disabled={!canEditSettings}
								onChange={(newValue) => {
									const updatedSettings = [...settings];
									updatedSettings[index] = {
										...setting,
										value: newValue
									};
									setSettings(updatedSettings);
								}}
								block
								style={{ width: '100%' }}
							/>
						</Tooltip>
					);
				}
			case settingFieldType.JSON:
				value = JSON.parse(setting.value);

				return (
					<ReactJson
						src={value}
						theme={'summerfruit:inverted'}
						displayArrayKey={false}
						displayDataTypes={false}
						onAdd={
							!isPermittedEditJson
								? false
								: (newEntry) => {
										const updatedSettings = [...settings];
										updatedSettings[index] = {
											...setting,
											value: JSON.stringify(newEntry.updated_src) // Update the value with the modified JSON data
										};

										setSettings(updatedSettings);
									}
						}
						onEdit={
							!isPermittedEditJson
								? false
								: (newValue) => {
										const updatedSettings = [...settings];
										updatedSettings[index] = {
											...setting,
											value: JSON.stringify(newValue.updated_src) // Update the value with the desired new value
										};
										setSettings(updatedSettings);
									}
						}
						onDelete={
							!isPermittedEditJson
								? false
								: (newValue) => {
										const updatedSettings = [...settings];
										updatedSettings[index] = {
											...setting,
											value: JSON.stringify(newValue.updated_src) // Update the value with the desired new value
										};
										setSettings(updatedSettings);
									}
						}
					/>
				);
			case settingFieldType.FILEUPLOAD:
				return (
					<Tooltip title={!canEditSettings ? "You don't have permission to edit settings" : ''}>
						<Upload
							name='file'
							maxCount={1}
							action={async (file) => await checkBeforeUpload(file)}
							accept='.png,.jpeg,.jpg,.webp'
							onRemove={handleRemoveUpload}
							fileList={fileList}
							disabled={!canEditSettings}
							style={{
								width: '100%'
							}}
						>
							<Button icon={<UploadOutlined />} disabled={!canEditSettings}>
								Click to Upload
							</Button>
						</Upload>
					</Tooltip>
				);
			case settingFieldType.DATE:
				const dateOptions = setting.valueOptions ? JSON.parse(setting.valueOptions) : {};
				return (
					<Tooltip
						title={
							!canEditSettings ? "You don't have permission to edit settings" : dateOptions.tooltip || ''
						}
					>
						<DatePicker
							value={setting.value === '0' ? dayjs() : dayjs(setting.value)}
							format={dateOptions.format || 'DD/MM/YYYY'}
							disabled={!canEditSettings || dateOptions.disabled || false}
							showTime={dateOptions.showTime ? { format: 'HH:mm' } : false}
							placeholder={dateOptions.placeholder || 'Select date'}
							disabledDate={(current) => {
								if (!current) return false;
								let isBlocked = false;

								if (dateOptions.disablePast) {
									isBlocked = isBlocked || current.isBefore(dayjs().startOf('day'));
								}
								if (dateOptions.disableFuture) {
									isBlocked = isBlocked || current.isAfter(dayjs().endOf('day'));
								}
								if (dateOptions.minDate) {
									isBlocked =
										isBlocked || current.isBefore(dayjs(dateOptions.minDate).startOf('day'));
								}
								if (dateOptions.maxDate) {
									isBlocked = isBlocked || current.isAfter(dayjs(dateOptions.maxDate).endOf('day'));
								}

								return isBlocked;
							}}
							onChange={(date, dateString) => {
								const updatedSettings = [...settings];
								updatedSettings[index] = {
									...setting,
									value: date ? date.toISOString() : null
								};
								setSettings(updatedSettings);
							}}
							className={'fieldDate'}
							style={dateOptions.style || { width: '100%' }}
						/>
					</Tooltip>
				);
			case settingFieldType.BUTTON:
				const options = setting.valueOptions ? JSON.parse(setting.valueOptions) : {};

				const handleClick = () => {
					handleButtonAction(options.action);
				};
				return (
					<Tooltip
						title={!canEditSettings ? "You don't have permission to edit settings" : options.tooltip || ''}
					>
						<Button
							{...options.style}
							className='fieldButton'
							onClick={handleClick}
							disabled={!canEditSettings}
						>
							{options.label}
						</Button>
					</Tooltip>
				);
			case settingFieldType.MULTIVALUE:
				return (
					<RenderMultiValue
						setting={setting}
						index={index}
						settings={settings}
						setSettings={setSettings}
						canEditSettings={canEditSettings}
					/>
				);
			case settingFieldType.CREDENTIAL:
				return (
					<RenderCredential
						setting={setting}
						index={index}
						settings={settings}
						setSettings={setSettings}
						canEditSettings={canEditSettings}
					/>
				);
			default:
				return <div />;
		}
	};

	return renderSettingByFieldType(setting);
};

export default RenderFieldTypeSetting;
